// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonWrapper } from "@insite/mobius/Button";
import { ClickableProps } from "@insite/mobius/Clickable";
import { Clickable } from "@insite/mobius/index";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@werner/App/Components/Common/XngZone";
import { color, ellipsis, mergeWithCss, xScreen } from "@werner/XDL/Utilities";
import parser from "html-react-parser";
import React, { FC, useState } from "react";
import { css } from "styled-components";

const enum fields {
    direction = "direction",
    image = "image",
    imageAlignment = "imageAlignment",
    enableBorderTop = "enableBorderTop",
    title = "title",
    content = "content",
    url = "url",
    openInNewTab = "openInNewTab",
    hasBorderTop = "hasBorderTop",
    contentId = "contentId",
}

interface OwnProps {
    fields: {
        [fields.direction]: string;
        [fields.image]: string;
        [fields.imageAlignment]: string;
        [fields.enableBorderTop]: boolean;
        [fields.title]: string;
        [fields.content]: string;
        [fields.url]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
        [fields.hasBorderTop]: boolean;
        [fields.contentId]: string;
    };
    extendedStyles?: BrandDetailsInfoCardStyles;
}

type Props = OwnProps & WidgetProps;

interface BrandDetailsInfoCardStyles {
    container?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    imageClickable?: ClickableProps;
    contentContainer?: InjectableCss;
    contentWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    content?: TypographyPresentationProps;
    buttonWrapper?: InjectableCss;
}

const brandDetailsInfoCardStyles: BrandDetailsInfoCardStyles = {
    container: {
        css: css`
            display: flex;
            ${({ layoutDirection }: any) => {
                if (layoutDirection === "horizontal") {
                    return css`
                        flex-direction: row;
                        margin-bottom: 20px;
                        gap: 24px;
                        ${xScreen("lte", "sm")} {
                            flex-direction: column;
                        }
                    `;
                }
                return css`
                    flex-direction: column;
                    justify-content: center;
                    height: auto;
                    max-height: 100%;
                `;
            }}

            ${({ borderTop }: any) =>
                borderTop &&
                css`
                    padding-top: 20px;
                    border-top: 1px solid ${color("common.ui1")};
                `}
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
            max-width: 520px;
            display: flex;
            margin-bottom: 20px;
            ${({ layoutDirection, imageAlignment }: any) => {
                if (layoutDirection === "horizontal") {
                    return css`
                        justify-content: ${imageAlignment};
                        max-height: 100%;
                        gap: 24px;
                        align-items: flex-start;
                        ${xScreen("lte", "sm")} {
                            margin-bottom: 0;
                            gap: 20px;
                        }
                    `;
                }

                return css`
                    justify-content: center;
                `;
            }}
            ${xScreen("lte", "xs")} {
                height: auto;
                max-height: 160px;
            }
        `,
    },
    image: {
        css: css`
            height: auto;
            width: auto;
            object-fit: contain;
            max-height: 100%;
            max-width: 100%;
        `,
    },
    contentContainer: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    contentWrapper: {
        css: css`
            margin-top: 12px;
            display: flex;
            align-items: flex-start;
            gap: 24px;
            ${xScreen("lte", "sm")} {
                flex-direction: column;
            }
            ${({ layoutDirection }: any) => {
                if (layoutDirection === "horizontal") {
                    return css`
                        flex-direction: column;
                    `;
                }
            }}
        `,
    },
    title: {
        forwardAs: "h4",
        size: 28,
        lineHeight: "32px",
        weight: 400,
        color: "text.main",
        css: css`
            ${ellipsis(1)};

            ${xScreen("lte", "md")} {
                font-size: 24px;
                line-height: 28px;
                weight: 400px;
            }
        `,
    },
    content: {
        variant: "p",
        size: 15,
        color: "text.main",
        css: css`
            letter-spacing: 0;
            line-height: 20px;
            max-width: 200ch;

            & > ${TypographyStyle} {
                font-size: inherit;
                font-weight: inherit;
                line-height: inherit;
                margin-bottom: 0;
            }

            ${({ hasTitle }: any) =>
                hasTitle &&
                css`
                    margin-bottom: 18px;
                `}
        `,
    },
    buttonWrapper: {
        css: css`
            max-width: 150px;
            width: 100%;
            max-height: 46px;
            height: 100%;
            &&&& ${ButtonWrapper} {
                min-width: 150px;
                max-width: 150px;
            }
        `,
    },
};

const BrandDetailsInfoCard: FC<Props> = ({ id, fields, extendedStyles }) => {
    const [styles] = useState(() => mergeWithCss(brandDetailsInfoCardStyles, extendedStyles));
    const link = useGetLink(fields.url);

    const onClickHandler = () => {
        const openInNewTab = fields.openInNewTab ? "_blank" : "_self";
        window.open(link.url, openInNewTab);
    };

    return (
        <StyledWrapper
            data-test-selector="brand-details-info-card-widget"
            {...styles.container}
            {...{ borderTop: fields.enableBorderTop, layoutDirection: fields.direction }}
        >
            <StyledWrapper
                {...styles.imageWrapper}
                {...{ layoutDirection: fields.direction, imageAlignment: fields.imageAlignment }}
            >
                <Clickable
                    {...styles.imageClickable}
                    onClick={onClickHandler}
                    aria-label={`${fields.callToActionLabel}`}
                >
                    <LazyImage src={fields.image} altText={translate(fields.callToActionLabel)} {...styles.image} />
                </Clickable>
            </StyledWrapper>
            <StyledWrapper {...styles.contentContainer}>
                {fields.title && <Typography {...styles.title}>{fields.title}</Typography>}
                <StyledWrapper {...styles.contentWrapper} {...{ layoutDirection: fields.direction }}>
                    <Typography {...styles.content} as="div" {...{ layoutDirection: fields.direction }}>
                        {parser(fields.content, parserOptions)}
                    </Typography>
                    <StyledWrapper {...styles.buttonWrapper}>
                        <XngZone
                            zoneName={`ButtonZone_${fields.title}`}
                            contentId={fields.contentId || id}
                            hint="Zone For Button"
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BrandDetailsInfoCard,
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Shared Info Card",
        icon: "link",
        fieldDefinitions: [
            {
                name: fields.direction,
                displayName: "Direction",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                defaultValue: "vertical",
                fieldType: "General",
            },
            {
                name: fields.enableBorderTop,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Translatable",
                isVisible: (item: HasFields) => item.fields.direction === "horizontal",
                variant: "toggle",
            },
            {
                name: fields.image,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.imageAlignment,
                displayName: "Image Alignment",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Left",
                        value: "flex-start",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "flex-end",
                    },
                ],
                defaultValue: "center",
                fieldType: "General",
                isVisible: (item: HasFields) => item.fields.direction === "horizontal",
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: false,
            },
            {
                name: fields.content,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.url,
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                fieldType: "Translatable",
            },
            {
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
